/* eslint-disable import/first */
require('libs/client/init-env').init() // eslint-disable-line

import './fonts.css'
import './base.scss'
import './highlight-shareable.scss'
import './help-center/gladly.scss'

import React from 'react'
import App, { AppContext, AppInitialProps } from 'next/app'
import Error from 'next/error'
import dynamic from 'next/dynamic'
import { NextRouter } from 'next/router'
import { SWRConfig } from 'swr'
import { ParsedUrlQuery } from 'querystring'
import { QueryClientProvider } from '@tanstack/react-query'

import { I18nextProvider } from '@mc/i18n'
import { setGeo, setRegion } from '@mc/geo'
import { setFeatureFlips } from '@mc/feature-flip'
import { FeatureFlipsProvider } from '@mc/feature-flip-react'
import { setExperiments, setExperimentsVariables } from '@mc/experiments'
import { ExperimentsProvider } from '@mc/experiments-react'
import { PromoBanner } from '@mc/promos'
import { StaticDataProvider } from '@mc/static-data-react'
// eslint-disable-next-line no-restricted-imports
import { mediaStyle } from '@mc/media'
import { GeoProvider } from '@mc/geo-react'
import { ErrorPage } from '@mc/error-page'
import { setServer, trackPage } from '@mc/track-event'
import ErrorBoundary from '@mc/error-boundary'
import { reportError as reportBrowserError } from '@mc/monitoring'
import { Head, NewRelic, Segment, Consent, AnonymousId } from '@mc/next-common'
import { getUserAttributes } from '@mc/next-user-attributes'
import { reportError } from '@mc/next-newrelic'
import { getGrowthBookFlags } from '@mc/next-growthbook'

import i18nInstance from 'i18n/i18nInstance'
import { requestSideEffects } from 'libs/request-side-effects'
import { fetcher } from 'libs/client/fetchers'
import { queryClient } from 'libs/client/query-client'
import { getUrlWithoutQueryParams } from 'libs/utils'
import { Navigation } from 'components/Navigation'
import { LoginSuccessDetection } from 'components/LoginSuccessDetection'
import { getSSRQuizOnlyOnceCookie } from 'libs/utils/quizOnlyOnce'
import { trackCoreWebVitals } from 'libs/utils/coreWebVitals'
import HotJar from 'components/Head/HotJar'
import { Turnstile } from 'components/Head/Turnstile'
import { Maze } from 'components/Head/Maze'

import { getGoogleOneTapPageLoadCookieSSR } from 'experiments/pen-3951_v2_google_email_capture'

const THEME_PAGE_CLASSES: Record<string, string> = {
  'mc-theme-sessions': 'mc-theme-sessions mc-theme-light',
}

const Footer = dynamic(() => import('components/Footer'))

setServer('nextjs')

type PageProps = {
  userAttributes: ReturnType<typeof getUserAttributes>
  experiments: Awaited<
    ReturnType<typeof getGrowthBookFlags>
  >['growthBookExperiments']
  experimentsVariables: Awaited<
    ReturnType<typeof getGrowthBookFlags>
  >['growthBookExperimentsVariables']
  featureFlips: Awaited<
    ReturnType<typeof getGrowthBookFlags>
  >['growthBookFeatureFlips']
  error?: globalThis.Error & { statusCode: number }
  userAgent?: string // pen-3951_v2
  annualPass?: unknown
  categories?: unknown
  trial?: unknown
  ctaNextPage?: unknown
  googleOneTapPageLoadCookie?: unknown // pen-3951_v2
}

type Props =
  | (AppInitialProps<PageProps> & {
      query?: ParsedUrlQuery
      pathname?: string
    })
  | {
      serverSideInitialPropsError: true
    }

type ComponentType = React.ComponentType<PageProps> & {
  pageSettings?: {
    title?: string
    pageName?: string
    analyticsLabel?: string
    pageLoadIntegrations?: Record<string, boolean>
    theme?: string
    footer?: {
      hide?: boolean
      kind?: 'regular' | 'simplified'
      zIndex?: number
    }
    hideNavigation?: boolean
    showPromo?: boolean
  }
  getLayout?: (
    page: React.ReactElement,
    config: { pageProps?: Partial<PageProps> },
  ) => React.ReactNode
}

declare global {
  interface Window {
    _mc?: {
      cwvV2?: boolean
      performanceTimings?: boolean
    }
  }
}

trackCoreWebVitals()

class Layout extends React.Component<
  Props & { Component: ComponentType; router: NextRouter; pathname: string }
> {
  constructor(
    props: Props & {
      Component: ComponentType
      router: NextRouter
      pathname: string
    },
  ) {
    super(props)
    try {
      if (!('serverSideInitialPropsError' in props)) {
        const {
          userAttributes,
          experiments,
          experimentsVariables,
          featureFlips,
        } = props.pageProps

        // set lib data for react
        setGeo(userAttributes.country)
        setRegion(userAttributes.region)
        setFeatureFlips(featureFlips)
        setExperiments(experiments)
        setExperimentsVariables(experimentsVariables)
      }
    } catch (e) {
      reportError(e, {
        rootLayout: 'true',
        location: 'Layout constructor',
        message: 'Error occurred in _app.page.js Layout constructor',
      })
    }
  }

  static async getInitialProps(appContext: AppContext): Promise<Props> {
    const appProps: AppInitialProps<PageProps> =
      await App.getInitialProps(appContext)
    const { query, req, res, pathname } = appContext.ctx

    // runs on server only
    if (req && res) {
      try {
        requestSideEffects(req, res, appContext.router)

        const userAttributes = getUserAttributes(
          appContext.ctx,
          appContext.router,
        )
        const {
          growthBookExperiments,
          growthBookExperimentsVariables,
          growthBookFeatureFlips,
        } = await getGrowthBookFlags(userAttributes, appContext.ctx)
        // set lib data for getServerSideProps
        setGeo(userAttributes.country)
        setRegion(userAttributes.region)
        setFeatureFlips({ ...growthBookFeatureFlips })
        setExperiments({ ...growthBookExperiments })
        setExperimentsVariables({
          ...growthBookExperimentsVariables,
        })

        const ctaNextPage = await getSSRQuizOnlyOnceCookie(appContext.ctx)

        const googleOneTapPageLoadCookie =
          await getGoogleOneTapPageLoadCookieSSR(appContext.ctx) // pen-3951_v2

        return {
          ...appProps,
          pageProps: {
            ...appProps.pageProps,
            experiments: {
              ...growthBookExperiments,
            },
            experimentsVariables: {
              ...growthBookExperimentsVariables,
            },
            featureFlips: {
              ...growthBookFeatureFlips,
            },
            userAttributes,
            ctaNextPage,
            userAgent: req.headers['user-agent'], // pen-3951_v2
            googleOneTapPageLoadCookie, // pen-3951_v2
          },
          query,
          pathname,
        }
      } catch (e) {
        reportError(e, {
          rootLayout: 'true',
          location: 'getInitialProps',
          message: 'Error occurred in _app.page.js getInitialProps',
        })
        return {
          serverSideInitialPropsError: true,
        }
      }
    }

    return { ...appProps }
  }

  render() {
    if ('serverSideInitialPropsError' in this.props) {
      return <ErrorPage />
    }

    const { Component, pageProps, query, router, pathname } = this.props

    const getLayout = Component.getLayout ?? ((page) => page)

    const pageTitle = Component.pageSettings?.title || 'MasterClass'
    const {
      annualPass,
      categories,
      trial,
      userAttributes,
      ctaNextPage,
      googleOneTapPageLoadCookie, // pen-3951_v2
    } = pageProps
    const promoCode = userAttributes?.promo_code
    const isLoggedIn = userAttributes?.is_logged_in
    const country = userAttributes?.country
    const region = userAttributes?.region
    const experimentsProviderValues = {
      experiments: pageProps?.experiments,
      experimentsVariables: pageProps?.experimentsVariables,
    }
    const featureFlips = pageProps?.featureFlips

    const cwvTracking =
      pageProps?.experiments?.cwv_reporting_onload === 'variant_1'

    const performanceTimings =
      pageProps?.experiments?.ev_perf_metrics === 'variant_1'

    const pageTheme = Component.pageSettings?.theme
    const mcPageClassName = `mc-page ${pageTheme ? THEME_PAGE_CLASSES[pageTheme] || '' : ''}`

    return (
      <ErrorBoundary
        fallback={() => <ErrorPage />}
        onError={(e) =>
          reportBrowserError(new globalThis.Error('Layout ErrorBoundary'), {
            error: String(e),
            stack: e?.stack as string,
          })
        }
      >
        <AnonymousId ajsAnonId={userAttributes?.userId} />
        <StaticDataProvider
          value={{
            annualPass,
            categories,
            trial,
            isLoggedIn,
            query,
            pathname,
            promoCode,
            ctaNextPage,
            userAttributes,
            googleOneTapPageLoadCookie, // pen-3951_v2
          }}
          providerKey={router.asPath}
        >
          <I18nextProvider i18n={i18nInstance} defaultNS='@mc/next'>
            <GeoProvider value={{ country, region }}>
              <FeatureFlipsProvider value={featureFlips}>
                <ExperimentsProvider value={experimentsProviderValues}>
                  <Head>
                    <title>{pageTitle}</title>
                    <style type='text/css'>{mediaStyle}</style>
                    <link rel='icon' href='/_next-public/favicon.ico' />
                    <link
                      rel='canonical'
                      href={getUrlWithoutQueryParams(router.asPath)}
                    />
                    <link
                      as='font'
                      crossOrigin=''
                      href='/_next-public/fonts/sohne-buch.woff2'
                      type='font/woff2'
                    />
                    <link
                      as='font'
                      crossOrigin=''
                      href='/_next-public/fonts/sohne-halbfett.woff2'
                      type='font/woff2'
                    />
                    <link
                      as='font'
                      crossOrigin=''
                      href='/_next-public/fonts/sohne-schmal-dreiviertelfett.woff2'
                      rel='preload'
                      type='font/woff2'
                    />
                    <script
                      dangerouslySetInnerHTML={{
                        // pen-5271_v1: core web vital reporting, updated method
                        __html: `window._mc = { cwvV2: ${cwvTracking}, performanceTimings: ${performanceTimings} };`,
                      }}
                    />
                  </Head>
                  <HotJar />
                  <NewRelic />
                  <Segment
                    analyticsLabel={Component.pageSettings?.analyticsLabel}
                    analyticsIntegrations={
                      Component.pageSettings?.pageLoadIntegrations
                    }
                    trackPage={trackPage}
                  />
                  <Consent />
                  <Turnstile />
                  <Maze />
                  <SWRConfig value={{ fetcher }}>
                    <QueryClientProvider client={queryClient}>
                      <LoginSuccessDetection>
                        <div className={mcPageClassName}>
                          {!Component.pageSettings?.hideNavigation && (
                            <Navigation />
                          )}
                          {Component.pageSettings?.showPromo && (
                            <PromoBanner pathname={pathname} />
                          )}
                          <main className='mc-page__content' id='mc-main'>
                            {!pageProps.error ? (
                              getLayout(<Component {...pageProps} />, {
                                pageProps,
                              })
                            ) : (
                              <Error
                                statusCode={pageProps.error.statusCode}
                                title={pageProps.error.message}
                              />
                            )}
                          </main>
                          {!Component.pageSettings?.footer?.hide && (
                            <Footer
                              kind={Component.pageSettings?.footer?.kind}
                            />
                          )}
                        </div>
                      </LoginSuccessDetection>
                    </QueryClientProvider>
                  </SWRConfig>
                </ExperimentsProvider>
              </FeatureFlipsProvider>
            </GeoProvider>
          </I18nextProvider>
        </StaticDataProvider>
      </ErrorBoundary>
    )
  }
}

export default Layout
